import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Global} from '@emotion/react';

import Config from '../Config';
import CustomFontsAPI from '../../common/api/custom-font';

export const THEME_VARIABLES = {
    base: {
        button: {borderRadius: 'full'},
        widgetContainerStyles: {
            eventWidget: {
                maxWidth: '700px',
                borderRadius: '12px',
            },
            pulseLoaderContainer: {
                backgroundColor: 'customColors.backgroundColors.primary',
            },
        },
        eventListStyles: {
            eventListContainer: {
                backgroundColor: 'customColors.backgroundColors.primary',
            },
            eventList: {
                padding: '0px',
            },
            eventListItem: {
                borderBottomWidth: '1px',
                borderBottomStyle: 'solid',
                borderBottomColor: 'accent',
                backgroundColor: 'customColors.backgroundColors.primary',
            },
            eventListItemDateContainer: {
                borderRight: 'none',
            },
            eventListItemText: {
                textContainer: {
                    paddingLeft: '10px',
                },
                title: {
                    fontWeight: 'semibold',
                    fontSize: '14px',
                },
                bottom: {
                    fontWeight: 'normal',
                },
            },
            eventListItemDate: {
                dateContainer: {
                    border: 'none',
                },
                month: {
                    color: 'red.default',
                    fontWeight: 'semibold',
                    lineHeight: '1.5',
                },
                day: {
                    color: 'text.primary.light',
                    lineHeight: '1',
                },
            },
        },
        noEventsStyles: {
            backgroundColor: 'customColors.backgroundColors.primary',
        },
        titleAreaStyles: {
            titleContainer: {
                borderBottomWidth: '1px',
                borderBottomStyle: 'solid',
                borderBottomColor: 'accent',
                backgroundColor: 'customColors.backgroundColors.secondary',
            },
        },
        footerAreaStyles: {
            footerContainer: {
                backgroundColor: 'customColors.backgroundColors.primary',
                borderTopWidth: '1px',
                borderTopStyle: 'solid',
                borderTopColor: 'accent',
            },
        },
    },
    square: {
        button: {
            borderRadius: '0px',
            height: '50px',
            width: {base: '100%', mobileXL: '154px'},
            lineHeight: '0',
            fontWeight: 'normal',
        },
        widgetContainerStyles: {
            eventWidget: {
                maxWidth: '795px',
                borderRadius: '0px',
            },
            pulseLoaderContainer: {
                backgroundColor: 'customColors.backgroundColors.primary',
            },
        },
        eventListStyles: {
            eventListContainer: {
                backgroundColor: 'customColors.backgroundColors.primary',
            },
            eventList: {
                marginX: '24px',
                maxHeight: '416px',
            },
            eventListItem: {
                paddingY: '10px',
                paddingLeft: '10px',
                paddingRight: '22px',
                height: {base: 'unset', mobileXL: '95px'},
                borderBottomWidth: '10px',
                borderBottomStyle: 'solid',
                backgroundColor: 'customColors.backgroundColors.secondary',
                borderBottomColor: 'customColors.backgroundColors.primary',
            },
            eventListItemText: {
                textContainer: {
                    paddingLeft: '20px',
                },
                title: {
                    fontWeight: 'normal',
                    fontSize: '16px',
                    lineHeight: '20px',
                    paddingBottom: '6px',
                },
                bottom: {
                    fontWeight: 'normal',
                    fontSize: '12px',
                },
            },
            eventListItemDate: {
                dateContainer: {
                    paddingRight: '20px',
                    paddingLeft: '12px',
                    borderRightWidth: '1px',
                    borderRightStyle: 'solid',
                    borderRightColor: 'customColors.text.primary',
                    height: '65px',
                },
                month: {
                    fontWeight: 'semibold',
                    fontSize: '14px',
                    lineHeight: '24px',
                    color: 'customColors.text.primary',
                },
                day: {
                    fontSize: '24px',
                    lineHeight: '1',
                    color: 'customColors.text.primary',
                },
            },
        },
        noEventsStyles: {
            backgroundColor: 'customColors.backgroundColors.secondary',
        },
        titleAreaStyles: {
            titleContainer: {
                borderBottom: 'none',
                backgroundColor: 'customColors.backgroundColors.primary',
            },
        },
        footerAreaStyles: {
            footerContainer: {
                backgroundColor: 'customColors.backgroundColors.primary',
                borderTop: 'none',
                paddingX: '32px',
                marginY: '8px',
            },
        },
    },
};

export const getThemeVariables = variant => {
    return THEME_VARIABLES[variant] || THEME_VARIABLES.base;
};

/**
 * Allows for the use of a custom font to be globally loaded.
 *
 * @function Fonts
 * @param {string} fontLink - The url for the CSS file that loads the custom font.
 * @returns {React.ReactElement} The `Global` {@link https://emotion.sh/docs/globals @emotion/react} component with the custom font applied
 */
export const Fonts = ({fontLink}) => {
    const [fontLinkContents, setFontLinkContents] = useState();

    useEffect(() => {
        const fetchData = async () => {
            setFontLinkContents(
                await CustomFontsAPI.loadCustomFontCSS(fontLink)
            );
        };

        fetchData();
    }, [fontLink]);

    return <Global styles={fontLinkContents} />;
};

Fonts.propTypes = {
    fontLink: PropTypes.string.isRequired,
};

const handleFontNames = fontName => {
    if (fontName.includes(',')) {
        const fontNamesArray = fontName.split(',');
        const stringifiedFontNamesArray = fontNamesArray.map(font =>
            font.replaceAll('+', ' ')
        );
        const parsedFontNames = stringifiedFontNamesArray.join(', ');

        return parsedFontNames;
    } else {
        return fontName.replaceAll('+', ' ');
    }
};

/**
 * @function createCustomTheme
 * @param {Object} affiliateData
 * @param {string} affiliateData.variant
 * @param {string} affiliateData.primaryBGColor
 * @param {string} affiliateData.secondaryBGColor
 * @param {string} affiliateData.secondaryHoverBGColor
 * @param {string} affiliateData.buttonColor
 * @param {string} affiliateData.buttonColorHover
 * @param {string} affiliateData.buttonTextColor
 * @param {string} affiliateData.primaryTextColor
 * @param {string} affiliateData.secondaryTextColor
 * @param {string} affiliateData.fontLink
 * @param {string} affiliateData.fontName
 * @returns {{colors: Object, components: Object, fonts: Object, partnerWidgetStyles: Object}} The custom theme Object to be applied over the ThemeProvider
 */
export const createCustomTheme = ({
    affiliateData: {
        variant,
        primaryBGColor,
        secondaryBGColor,
        secondaryHoverBGColor,
        buttonColor,
        buttonColorHover,
        buttonTextColor,
        primaryTextColor,
        secondaryTextColor,
        fontLink,
        fontName,
    },
}) => {
    const {
        button: {
            borderRadius: variantButtonBorderRadius,
            height: variantButtonHeight,
            width: variantButtonWidth,
            lineHeight: variantButtonLineHeight,
            fontWeight: variantButtonFontWeight,
        },
        widgetContainerStyles: {eventWidget, pulseLoaderContainer},
        titleAreaStyles: {titleContainer},
        eventListStyles: {
            eventList,
            eventListItem,
            eventListItemText,
            eventListItemDate,
            eventListContainer,
        },
        noEventsStyles,
        footerAreaStyles,
    } = getThemeVariables(variant);

    const colors = {
        customColors: {
            backgroundColors: {
                primary: primaryBGColor || Config.primaryBGColor,
                secondary: secondaryBGColor || Config.secondaryBGColor,
                secondaryOnHover:
                    secondaryHoverBGColor || Config.secondaryHoverBGColor,
            },
            button: {
                buttonColor: buttonColor || Config.buttonColor,
                buttonColorOnHover: buttonColorHover || Config.buttonColorHover,
                buttonTextColor: buttonTextColor || Config.buttonTextColor,
            },
            text: {
                primary: primaryTextColor || Config.primaryTextColor,
                secondary: secondaryTextColor || Config.secondaryTextColor,
            },
        },
    };

    const components = {
        Button: {
            baseStyle: {
                borderRadius: variantButtonBorderRadius,
                height: variantButtonHeight,
                width: variantButtonWidth,
                lineHeight: variantButtonLineHeight,
                fontWeight: variantButtonFontWeight,
            },
            variants: {
                primary: {
                    bg: 'customColors.button.buttonColor',
                    borderColor: 'customColors.button.buttonColor',
                    color: 'customColors.button.buttonTextColor',
                    _hover: {
                        bg: 'customColors.button.buttonColorOnHover',
                        borderColor: 'customColors.button.buttonColorOnHover',
                        color: 'customColors.button.buttonTextColor',
                    },
                },
            },
        },
    };

    const fonts = {
        heading: `${
            fontLink && fontName ? handleFontNames(fontName) : 'Open Sans'
        }, sans-serif`,
        body: `${
            fontLink ? handleFontNames(fontName) : 'Open Sans'
        }, sans-serif`,
    };

    const partnerWidgetStyles = {
        widgetContainerStyles: {
            eventWidget: {
                ...eventWidget,
                backgroundColor: 'customColors.backgroundColors.primary',
            },
            pulseLoaderContainer,
        },
        titleAreaStyles: {
            titleContainer: {
                ...titleContainer,
            },
            title: {
                color: 'customColors.text.primary',
            },
        },
        eventListStyles: {
            eventListContainer,
            eventList: {
                ...eventList,
                backgroundColor: 'customColors.backgroundColors.primary',
            },
            eventListItem: {
                ...eventListItem,
                _hover: {
                    backgroundColor:
                        'customColors.backgroundColors.secondaryOnHover',
                },
            },
            eventListItemDetails: {
                sx: {
                    '> p': {
                        color: 'customColors.text.secondary',
                    },
                },
            },
            eventListItemDateContainer: {
                borderRight: 'none',
            },
            eventListItemText: {
                ...eventListItemText,
                title: {
                    ...eventListItemText.title,
                    color: 'customColors.text.primary',
                },
                bottom: {
                    ...eventListItemText.bottom,
                    color: 'customColors.text.secondary',
                },
            },
            eventListItemDate: {
                ...eventListItemDate,
            },
        },
        noEventsStyles,
        footerAreaStyles,
    };

    const customTheme = {
        colors,
        components,
        fonts,
        partnerWidgetStyles,
    };

    return customTheme;
};
